import { useCallback, useEffect, useRef, useState } from 'react';
import { Mic } from 'lucide-react';
import { motion } from 'framer-motion';
import { LiveAudioWaveVisualizer } from '@/components/session-call/LiveAudioWaveVisualizer';
import { cn } from '@/lib/utils';

type P2PTranscriptionHeroCircleProps = {
	stream: MediaStream | null;
	isIdle: boolean;
	isActive: boolean;
	isBusy: boolean;
	onClick?: () => void;
	disabled?: boolean;
};

type HeroPulse = {
	id: number;
	maxScale: number;
	startOpacity: number;
};

const HERO_BLUE = '#38bdf8';
const HERO_BLUE_GLOW = 'rgba(56,189,248,0.45)';
const LOUD_VOICE_LEVEL = 0.36;
const MAX_ACTIVE_PULSES = 2;

const clamp = (value: number, min: number, max: number) =>
	Math.min(max, Math.max(min, value));

const resolvePulseIntensity = (level: number) =>
	clamp((level - LOUD_VOICE_LEVEL) / 0.58, 0, 1);

const P2PTranscriptionHeroCircle = ({
	stream,
	isIdle,
	isActive,
	isBusy,
	onClick,
	disabled,
}: P2PTranscriptionHeroCircleProps) => {
	const isClickable = isIdle && onClick && !disabled && !isBusy;
	const [pulses, setPulses] = useState<HeroPulse[]>([]);
	const lastPulseAtRef = useRef(0);
	const pulseIdRef = useRef(0);
	const timeoutRef = useRef<number | null>(null);

	useEffect(() => {
		return () => {
			if (timeoutRef.current !== null) {
				window.clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const removePulse = useCallback((id: number) => {
		setPulses((current) => current.filter((pulse) => pulse.id !== id));
	}, []);

	const spawnPulse = useCallback((intensity: number, scaleBoost = 0) => {
		const id = pulseIdRef.current + 1;
		pulseIdRef.current = id;
		setPulses((current) => {
			if (current.length >= MAX_ACTIVE_PULSES) return current;
			return [
				...current,
				{
					id,
					maxScale: 1.12 + intensity * 0.16 + scaleBoost,
					startOpacity: 0.4 + intensity * 0.4,
				},
			];
		});
	}, []);

	const handleAudioLevel = useCallback(
		(level: number) => {
			if (!isActive || level < LOUD_VOICE_LEVEL) return;

			const intensity = resolvePulseIntensity(level);
			const cooldownMs = 520 - intensity * 280;

			const now = Date.now();
			if (now - lastPulseAtRef.current < cooldownMs) return;
			lastPulseAtRef.current = now;

			spawnPulse(intensity);

			if (intensity >= 0.75) {
				if (timeoutRef.current !== null) {
					window.clearTimeout(timeoutRef.current);
				}
				timeoutRef.current = window.setTimeout(
					() => spawnPulse(intensity, 0.03),
					110,
				);
			}
		},
		[isActive, spawnPulse],
	);

	return (
		<div className="relative flex aspect-square size-44 shrink-0 items-center justify-center sm:size-48">
			<button
				type="button"
				onClick={isClickable ? onClick : undefined}
				disabled={!isClickable}
				className={cn(
					'relative flex aspect-square size-full items-center justify-center',
					isClickable &&
						'cursor-pointer hover:scale-[1.02] active:scale-[0.98]',
					!isClickable && 'cursor-default',
				)}
				aria-label={
					isIdle
						? 'Iniciar transcrição'
						: isBusy
						  ? 'Conectando transcrição'
						  : 'Transcrição ativa'
				}
			>
				{isActive
					? pulses.map((pulse) => (
							<motion.span
								key={pulse.id}
								aria-hidden
								className="pointer-events-none absolute inset-3 rounded-full border-2 border-[#38bdf8]/55"
								initial={{
									scale: 1,
									opacity: pulse.startOpacity,
								}}
								animate={{ scale: pulse.maxScale, opacity: 0 }}
								transition={{
									duration: 0.7 + pulse.maxScale * 0.08,
									ease: 'easeOut',
								}}
								onAnimationComplete={() =>
									removePulse(pulse.id)
								}
							/>
					  ))
					: null}

				{isIdle ? (
					<>
						<span
							aria-hidden
							className="absolute inset-3 rounded-full blur-2xl"
							style={{ backgroundColor: `${HERO_BLUE}33` }}
						/>
						<span
							className="absolute inset-3 rounded-full border-2 border-[#38bdf8]/60 bg-[#0c1929]/90"
							style={{
								boxShadow: `0 0 56px ${HERO_BLUE_GLOW}, inset 0 0 36px rgba(56,189,248,0.08)`,
							}}
						/>
						<Mic
							className="relative z-10 size-12 text-[#38bdf8] sm:size-14"
							strokeWidth={2.25}
						/>
					</>
				) : (
					<>
						<span
							aria-hidden
							className="absolute inset-3 rounded-full blur-xl"
							style={{
								backgroundColor: isActive
									? `${HERO_BLUE}22`
									: `${HERO_BLUE}11`,
							}}
						/>
						<span
							className={cn(
								'absolute inset-3 rounded-full border-2 border-[#38bdf8]/70 bg-[#0c1929]/85',
								isBusy && 'animate-pulse',
							)}
							style={{
								boxShadow: `0 0 48px ${HERO_BLUE_GLOW}, inset 0 0 32px rgba(56,189,248,0.1)`,
							}}
						/>
						<div className="relative z-10 flex aspect-square w-[62%] items-center justify-center">
							<LiveAudioWaveVisualizer
								stream={stream}
								active={isActive}
								variant="hero"
								onAudioLevel={handleAudioLevel}
								ariaLabel="Visualização de áudio da transcrição"
							/>
						</div>
					</>
				)}
			</button>
		</div>
	);
};

export { P2PTranscriptionHeroCircle };
